import { generateLeaseKey } from './leaseKeyUtils';

/**
 * Builds a countyMap structure from search results
 * @param {Object} searchResults - Search results data
 * @param {Object} includedTables - Object with boolean flags for each table type
 * @returns {Object} { countyMap, countyCounts }
 */
export const buildCountyMap = (searchResults, includedTables) => {
  if (!searchResults?.data) {
    return { countyMap: new Map(), countyCounts: new Map() };
  }

  // Helper function to check if a well has valid coordinates
  const hasValidCoordinates = (well) => {
    const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
    const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
    return !isNaN(lat) && !isNaN(lng);
  };

  const { wells, production, inspections, violations, severances, spills } = searchResults.data;
  
  // Create a map of county -> lease number -> aggregated data
  const countyMap = new Map();
  
  // Process Wells data
  if (includedTables.wells && wells && wells.length > 0) {
    wells.forEach(well => {
      const county = well['County'] || well.county || 'Unknown County';
      const district = well['District'] || well.district || '00';
      const leaseNumber = well['Lease Number'] || well.lease_number || 'Unknown Lease';
      const leaseName = well['Lease Name'] || well.lease_name || 'Unknown Lease';
      const apiNumber = well['API Number'] || well.api_number || well.api || 'Unknown API';
      
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!countyMap.has(county)) {
        countyMap.set(county, new Map());
      }
      
      const countyData = countyMap.get(county);
      if (!countyData.has(leaseKey)) {
        countyData.set(leaseKey, {
          leaseNumber,
          leaseName: [leaseName],
          county,
          district,
          wells: [],
          production: null,
          inspections: null,
          violations: null,
          severances: null,
          spills: null,
          isMapped: false
        });
      } else {
        const lease = countyData.get(leaseKey);
        if (leaseName && leaseName.trim() && leaseName !== 'Unknown Lease') {
          const trimmedName = leaseName.trim().toLowerCase();
          const isUnique = !lease.leaseName.some(name => name.trim().toLowerCase() === trimmedName);
          if (isUnique) {
            lease.leaseName.push(leaseName);
            lease.leaseName.sort((a, b) => a.localeCompare(b));
          }
        }
      }
      
      const lease = countyData.get(leaseKey);
      const pluggingCost = well['PLUGGING_COST'] || well['plugging_cost'] || well['Plugging Cost'] || null;
      lease.wells.push({
        apiNumber,
        wellName: well['Well Name'] || well.well_name || well.name || 'Unknown Well',
        wellGroup: well['Well Group'] || well.well_group || well.well_status || well.status || 'Unknown',
        hasCoordinates: hasValidCoordinates(well),
        inactiveYears: well.inactive_years || null,
        plugDate: well['Plug Date'] || well.plug_date || well.plugged_date || null,
        pluggingCost: (pluggingCost !== null && pluggingCost !== undefined && pluggingCost !== '' && 
                      typeof pluggingCost === 'number' && !isNaN(pluggingCost)) ? pluggingCost : null
      });
      
      if (hasValidCoordinates(well)) {
        lease.isMapped = true;
      }
    });
  }
  
  // Process Production data - calculate averages
  if (includedTables.production && production && production.length > 0) {
    const productionByLease = new Map();
    
    production.forEach(record => {
      const district = record['District'] || record.district || '00';
      const leaseNumber = record['Lease Number'] || record.lease_number || 'Unknown Lease';
      const leaseName = record['Lease Name'] || record.lease_name || 'Unknown Lease';
      const county = record['County'] || record.county || 'Unknown County';
      const key = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!productionByLease.has(key)) {
        productionByLease.set(key, {
          county,
          district,
          leaseNumber,
          leaseName,
          records: []
        });
      }
      
      productionByLease.get(key).records.push({
        oilBbl: parseFloat(record['Oil BBL'] || record.oil_bbl || 0),
        gasMcf: parseFloat(record['Gas MCF'] || record.gas_mcf || 0),
        flareGasMcf: parseFloat(record['Flare Gas MCF'] || record.flare_gas_mcf || 0),
        fuelGasMcf: parseFloat(record['Fuel Gas MCF'] || record.fuel_gas_mcf || 0),
        daysInMonth: parseFloat(record['Days in Month'] || record.days_month || 30),
        productionMonth: record['Production Month'] || record.production_month || 'Unknown'
      });
    });
    
    productionByLease.forEach((prodData, key) => {
      const { county, district, leaseNumber, leaseName, records } = prodData;
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!countyMap.has(county)) {
        countyMap.set(county, new Map());
      }
      
      const countyData = countyMap.get(county);
      
      if (!countyData.has(leaseKey)) {
        countyData.set(leaseKey, {
          leaseNumber,
          leaseName: [leaseName],
          county,
          district,
          wells: [],
          production: null,
          inspections: null,
          violations: null,
          severances: null,
          spills: null,
          isMapped: false
        });
      } else {
        const lease = countyData.get(leaseKey);
        if (leaseName && leaseName.trim() && leaseName !== 'Unknown Lease') {
          const trimmedName = leaseName.trim().toLowerCase();
          const isUnique = !lease.leaseName.some(name => name.trim().toLowerCase() === trimmedName);
          if (isUnique) {
            lease.leaseName.push(leaseName);
            lease.leaseName.sort((a, b) => a.localeCompare(b));
          }
        }
      }
      
      const totalOilBbl = records.reduce((sum, r) => sum + r.oilBbl, 0);
      const totalGasMcf = records.reduce((sum, r) => sum + r.gasMcf, 0);
      const totalFlareGasMcf = records.reduce((sum, r) => sum + r.flareGasMcf, 0);
      const totalFuelGasMcf = records.reduce((sum, r) => sum + r.fuelGasMcf, 0);
      
      const uniqueMonths = new Set(records.map(r => r.productionMonth));
      const totalDaysInMonth = Array.from(uniqueMonths).reduce((sum, month) => {
        const firstRecord = records.find(r => r.productionMonth === month);
        return sum + (firstRecord?.daysInMonth || 0);
      }, 0);
      
      const avgOilBblPerDay = totalDaysInMonth > 0 ? totalOilBbl / totalDaysInMonth : 0;
      const avgGasMcfPerDay = totalDaysInMonth > 0 ? totalGasMcf / totalDaysInMonth : 0;
      const avgFlaringPerDay = totalDaysInMonth > 0 ? totalFlareGasMcf / totalDaysInMonth : 0;
      const avgFuelGasPerDay = totalDaysInMonth > 0 ? totalFuelGasMcf / totalDaysInMonth : 0;
      const avgFlaringIntensity = totalGasMcf > 0 ? (totalFlareGasMcf / totalGasMcf) * 100 : 0;
      const avgFuelGasIntensity = totalGasMcf > 0 ? (totalFuelGasMcf / totalGasMcf) * 100 : 0;
      
      countyData.get(leaseKey).production = {
        avgOilBblPerDay,
        avgGasMcfPerDay,
        avgFlaringPerDay,
        avgFuelGasPerDay,
        avgFlaringIntensity,
        avgFuelGasIntensity,
        recordCount: records.length
      };
    });
  }
  
  // Process Inspections data - calculate compliance stats
  if (includedTables.inspections && inspections && inspections.length > 0) {
    inspections.forEach(record => {
      const district = record['District'] || record.district || '00';
      const leaseNumber = record['Lease Number'] || record.lease_number || 'Unknown Lease';
      const leaseName = record['Lease Facility Name'] || record.lease_facility_name || record['Lease Name'] || record.lease_name || 'Unknown Lease';
      const county = record['County'] || record.county || 'Unknown County';
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!countyMap.has(county)) {
        countyMap.set(county, new Map());
      }
      
      const countyData = countyMap.get(county);
      
      if (!countyData.has(leaseKey)) {
        countyData.set(leaseKey, {
          leaseNumber,
          leaseName: [leaseName],
          county,
          district,
          wells: [],
          production: null,
          inspections: null,
          violations: null,
          severances: null,
          spills: null,
          isMapped: false
        });
      } else {
        const lease = countyData.get(leaseKey);
        if (leaseName && leaseName.trim() && leaseName !== 'Unknown Lease') {
          const trimmedName = leaseName.trim().toLowerCase();
          const isUnique = !lease.leaseName.some(name => name.trim().toLowerCase() === trimmedName);
          if (isUnique) {
            lease.leaseName.push(leaseName);
            lease.leaseName.sort((a, b) => a.localeCompare(b));
          }
        }
      }
      
      const lease = countyData.get(leaseKey);
      
      if (!lease.inspections) {
        lease.inspections = {
          count: 0,
          compliantCount: 0,
          complianceRate: 0
        };
      }
      
      lease.inspections.count++;
      const compliance = record['Compliance'] || record.compliance || 'Unknown';
      if (compliance?.toUpperCase() === 'YES') {
        lease.inspections.compliantCount++;
      }
      
      lease.inspections.complianceRate = 
        lease.inspections.count > 0 ? 
        (lease.inspections.compliantCount / lease.inspections.count) * 100 : 0;
    });
  }
  
  // Process Violations data - calculate violation stats
  if (includedTables.violations && violations && violations.length > 0) {
    violations.forEach(record => {
      const district = record['District'] || record.district || '00';
      const leaseNumber = record['Lease Number'] || record.lease_number || 'Unknown Lease';
      const leaseName = record['Lease Facility Name'] || record.lease_facility_name || record['Lease Name'] || record.lease_name || 'Unknown Lease';
      const county = record['County'] || record.county || 'Unknown County';
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!countyMap.has(county)) {
        countyMap.set(county, new Map());
      }
      
      const countyData = countyMap.get(county);
      
      if (!countyData.has(leaseKey)) {
        countyData.set(leaseKey, {
          leaseNumber,
          leaseName: [leaseName],
          county,
          district,
          wells: [],
          production: null,
          inspections: null,
          violations: null,
          severances: null,
          spills: null,
          isMapped: false
        });
      } else {
        const lease = countyData.get(leaseKey);
        if (leaseName && leaseName.trim() && leaseName !== 'Unknown Lease') {
          const trimmedName = leaseName.trim().toLowerCase();
          const isUnique = !lease.leaseName.some(name => name.trim().toLowerCase() === trimmedName);
          if (isUnique) {
            lease.leaseName.push(leaseName);
            lease.leaseName.sort((a, b) => a.localeCompare(b));
          }
        }
      }
      
      const lease = countyData.get(leaseKey);
      
      if (!lease.violations) {
        lease.violations = {
          count: 0,
          majorCount: 0,
          reinspectionCompliantCount: 0,
          reinspectionComplianceRate: 0,
          notCompliantOrNotReinspected: 0,
          totalReinspections: 0,
          ruleCounts: {}
        };
      }
      
      lease.violations.count++;
      
      if (record.major_violation_indicator === 'Y') {
        lease.violations.majorCount++;
      }
      
      const violatedRule = record['Violated Rule Description'] || record.violated_rule_description;
      if (violatedRule && violatedRule.toString().trim() !== '') {
        const rule = violatedRule.toString().trim();
        lease.violations.ruleCounts[rule] = (lease.violations.ruleCounts[rule] || 0) + 1;
      }
      
      const reinspectionCompliance = record.compliant_on_reinspection || 'Unknown';
      
      if (reinspectionCompliance && reinspectionCompliance !== 'Unknown' && 
          reinspectionCompliance.toString().trim() !== '' && 
          reinspectionCompliance.toString().trim() !== '--') {
        lease.violations.totalReinspections++;
        
        if (reinspectionCompliance.toString().toUpperCase() === 'Y') {
          lease.violations.reinspectionCompliantCount++;
        }
      }
      
      lease.violations.reinspectionComplianceRate = 
        lease.violations.totalReinspections > 0 ? 
        (lease.violations.reinspectionCompliantCount / lease.violations.totalReinspections) * 100 : 0;
      
      lease.violations.notCompliantOrNotReinspected = 
        lease.violations.count - lease.violations.reinspectionCompliantCount;
    });
    
    // Calculate leading cause for violations after all violations are processed
    for (const [, countyData] of countyMap.entries()) {
      for (const [, leaseData] of countyData.entries()) {
        if (leaseData.violations && leaseData.violations.ruleCounts) {
          const ruleCounts = leaseData.violations.ruleCounts;
          const sortedRules = Object.entries(ruleCounts)
            .filter(([rule]) => rule && rule.trim() !== '')
            .sort(([, a], [, b]) => b - a);
          
          if (sortedRules.length > 0) {
            const [leadingRule, count] = sortedRules[0];
            leaseData.violations.leadingCause = leadingRule;
            leaseData.violations.leadingCausePercentage = 
              leaseData.violations.count > 0 ? (count / leaseData.violations.count) * 100 : 0;
          }
        }
      }
    }
  }
  
  // Process Severances data - calculate severance stats
  if (includedTables.severances && severances && severances.length > 0) {
    const severancesByLease = new Map();
    
    severances.forEach(record => {
      const district = record['District'] || record.district || '00';
      const leaseNumber = record['Lease Number'] || record.lease_number || 'Unknown Lease';
      const leaseName = record['Lease Name'] || record.lease_name || 'Unknown Lease';
      const county = record['County'] || record.county || 'Unknown County';
      const key = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!severancesByLease.has(key)) {
        severancesByLease.set(key, {
          county,
          district,
          leaseNumber,
          leaseName,
          records: []
        });
      }
      
      severancesByLease.get(key).records.push({
        resolved: !!(record['Violation Resolved Date'] || record.violation_resolved_date),
        certifiedLetterDate: record['Certified Letter Date'] || record.certified_letter_date,
        resolvedDate: record['Violation Resolved Date'] || record.violation_resolved_date,
        reason: record['Issue Reason'] || record.issue_reason || 'Unknown'
      });
    });
    
    severancesByLease.forEach((sevData, key) => {
      const { county, district, leaseNumber, leaseName, records } = sevData;
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!countyMap.has(county)) {
        countyMap.set(county, new Map());
      }
      
      const countyData = countyMap.get(county);
      
      if (!countyData.has(leaseKey)) {
        countyData.set(leaseKey, {
          leaseNumber,
          leaseName: [leaseName],
          county,
          district,
          wells: [],
          production: null,
          inspections: null,
          violations: null,
          severances: null,
          spills: null,
          isMapped: false
        });
      } else {
        const lease = countyData.get(leaseKey);
        if (leaseName && leaseName.trim() && leaseName !== 'Unknown Lease') {
          const trimmedName = leaseName.trim().toLowerCase();
          const isUnique = !lease.leaseName.some(name => name.trim().toLowerCase() === trimmedName);
          if (isUnique) {
            lease.leaseName.push(leaseName);
            lease.leaseName.sort((a, b) => a.localeCompare(b));
          }
        }
      }
      
      const resolvedCount = records.filter(r => r.resolved).length;
      const resolvedRate = (resolvedCount / records.length) * 100;
      
      const resolvedRecords = records.filter(r => r.resolved && r.certifiedLetterDate && r.resolvedDate);
      let totalDaysToResolve = 0;
      let daysResolvedCount = 0;
      
      resolvedRecords.forEach(r => {
        const certifiedDate = new Date(r.certifiedLetterDate);
        const resolvedDate = new Date(r.resolvedDate);
        
        if (!isNaN(certifiedDate.getTime()) && !isNaN(resolvedDate.getTime())) {
          const timeDiff = resolvedDate.getTime() - certifiedDate.getTime();
          const days = timeDiff / (1000 * 60 * 60 * 24);
          if (days >= 0) {
            totalDaysToResolve += days;
            daysResolvedCount++;
          }
        }
      });
      
      const avgDaysToResolve = daysResolvedCount > 0 ? Math.round(totalDaysToResolve / daysResolvedCount) : 0;
      
      const reasonCounts = {};
      records.forEach(r => {
        const reason = r.reason;
        if (reason && reason.toString().trim() !== '') {
          const reasonKey = reason.toString().trim();
          reasonCounts[reasonKey] = (reasonCounts[reasonKey] || 0) + 1;
        }
      });
      
      const entries = Object.entries(reasonCounts);
      let leadingCause = '';
      let leadingCausePercentage = 0;
      
      if (entries.length > 0) {
        const sortedEntries = entries.sort((a, b) => b[1] - a[1]);
        leadingCause = sortedEntries[0][0];
        leadingCausePercentage = ((sortedEntries[0][1] / records.length) * 100).toFixed(1);
      }
      
      countyData.get(leaseKey).severances = {
        count: records.length,
        resolvedRate: resolvedRate,
        avgDaysToResolve: avgDaysToResolve,
        leadingCause: leadingCause,
        leadingCausePercentage: leadingCausePercentage
      };
    });
  }
  
  // Process Spills data - calculate spill stats
  if (includedTables.spills && spills && spills.length > 0) {
    const spillsByLease = new Map();
    
    spills.forEach(record => {
      const district = record['District'] || record.district || '00';
      const leaseNumber = record['Lease Number'] || record.lease_number || 'Unknown Lease';
      const leaseName = record['Lease Name'] || record.lease_name || 'Unknown Lease';
      const county = record['County'] || record.county || 'Unknown County';
      const key = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!spillsByLease.has(key)) {
        spillsByLease.set(key, {
          county,
          district,
          leaseNumber,
          leaseName,
          records: []
        });
      }
      
      spillsByLease.get(key).records.push({
        grossLoss: parseFloat(record['Gross Loss'] || record.gross_loss || 0),
        recovered: parseFloat(record['Recovered'] || record.recovered || 0),
        lost: parseFloat(record['Net Loss'] || record.net_loss || 0),
        impactsWater: record['Water Impact'] || record.water_impact || 'N',
        cause: record['Cause of Loss'] || record.cause_of_loss || 'Unknown',
        facility: record['Facility Type'] || record.facility_type || 'Unknown',
        liquidType: record['Liquid Type'] || record.liquid_type || 'Unknown'
      });
    });
    
    spillsByLease.forEach((spillData, key) => {
      const { county, district, leaseNumber, leaseName, records } = spillData;
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      
      if (!countyMap.has(county)) {
        countyMap.set(county, new Map());
      }
      
      const countyData = countyMap.get(county);
      
      if (!countyData.has(leaseKey)) {
        countyData.set(leaseKey, {
          leaseNumber,
          leaseName: [leaseName],
          county,
          district,
          wells: [],
          production: null,
          inspections: null,
          violations: null,
          severances: null,
          spills: null,
          isMapped: false
        });
      } else {
        const lease = countyData.get(leaseKey);
        if (leaseName && leaseName.trim() && leaseName !== 'Unknown Lease') {
          const trimmedName = leaseName.trim().toLowerCase();
          const isUnique = !lease.leaseName.some(name => name.trim().toLowerCase() === trimmedName);
          if (isUnique) {
            lease.leaseName.push(leaseName);
            lease.leaseName.sort((a, b) => a.localeCompare(b));
          }
        }
      }
      
      const totalGrossLoss = records.reduce((sum, r) => sum + r.grossLoss, 0);
      const totalRecovered = records.reduce((sum, r) => sum + r.recovered, 0);
      const totalLost = records.reduce((sum, r) => sum + r.lost, 0);
      const avgSpillVolume = totalGrossLoss / records.length;
      const recoveryRate = totalGrossLoss > 0 ? (totalRecovered / totalGrossLoss) * 100 : 0;
      const lossRate = totalGrossLoss > 0 ? (totalLost / totalGrossLoss) * 100 : 0;
      const waterImpactCount = records.filter(r => {
        const impact = r.impactsWater;
        return impact && impact.toString().toUpperCase() === 'YES';
      }).length;
      
      const causeCounts = {};
      const facilityCounts = {};
      const liquidCounts = {};
      
      records.forEach(r => {
        const cause = r.cause;
        const facility = r.facility;
        const liquidType = r.liquidType;
        
        if (cause && cause.toString().trim() !== '') {
          const causeKey = cause.toString().trim();
          causeCounts[causeKey] = (causeCounts[causeKey] || 0) + 1;
        }
        
        if (facility && facility.toString().trim() !== '') {
          const facilityKey = facility.toString().trim();
          facilityCounts[facilityKey] = (facilityCounts[facilityKey] || 0) + 1;
        }
        
        if (liquidType && liquidType.toString().trim() !== '') {
          const liquidKey = liquidType.toString().trim();
          liquidCounts[liquidKey] = (liquidCounts[liquidKey] || 0) + 1;
        }
      });
      
      const causeEntries = Object.entries(causeCounts);
      const facilityEntries = Object.entries(facilityCounts);
      const liquidEntries = Object.entries(liquidCounts);
      
      let leadingCause = '';
      let leadingCausePercentage = 0;
      let topFacility = '';
      let topFacilityPercentage = 0;
      let topLiquidType = '';
      let topLiquidTypePercentage = 0;
      
      if (causeEntries.length > 0) {
        const sortedCauseEntries = causeEntries.sort((a, b) => b[1] - a[1]);
        leadingCause = sortedCauseEntries[0][0];
        leadingCausePercentage = ((sortedCauseEntries[0][1] / records.length) * 100).toFixed(1);
      }
      
      if (facilityEntries.length > 0) {
        const sortedFacilityEntries = facilityEntries.sort((a, b) => b[1] - a[1]);
        topFacility = sortedFacilityEntries[0][0];
        topFacilityPercentage = ((sortedFacilityEntries[0][1] / records.length) * 100).toFixed(1);
      }
      
      if (liquidEntries.length > 0) {
        const sortedLiquidEntries = liquidEntries.sort((a, b) => b[1] - a[1]);
        topLiquidType = sortedLiquidEntries[0][0];
        topLiquidTypePercentage = ((sortedLiquidEntries[0][1] / records.length) * 100).toFixed(1);
      }
      
      countyData.get(leaseKey).spills = {
        count: records.length,
        avgSpillVolume: avgSpillVolume,
        totalReportedVolume: totalGrossLoss,
        recoveryRate: recoveryRate,
        lossRate: lossRate,
        waterImpactCount: waterImpactCount,
        leadingCause: leadingCause,
        leadingCausePercentage: leadingCausePercentage,
        topFacility: topFacility,
        topFacilityPercentage: topFacilityPercentage,
        topLiquidType: topLiquidType,
        topLiquidTypePercentage: topLiquidTypePercentage
      };
    });
  }
  
  // Pre-compute county-level counts for performance
  const countyCounts = new Map();
  countyMap.forEach((leases, county) => {
    let totalWells = 0;
    leases.forEach((lease) => {
      totalWells += lease.wells.length;
    });
    countyCounts.set(county, {
      leaseCount: leases.size,
      wellCount: totalWells
    });
  });
  
  return { countyMap, countyCounts };
};

